import { useEffect, useId, useState } from 'react';
import type { ReactNode } from 'react';

/**
 * A section of the task panel that folds to one line.
 *
 * Drop 35 (docs/design-rule.md). The panel used to open Description, then
 * Attachments, then Subtasks, then Followers, then Comments — and the two
 * sections in the middle were EMPTY on most tasks yet still spent a category
 * dropdown, two buttons and a four-field add form. Now an empty section is a
 * line: its name, "none", and the one thing you can do about it on the right.
 *
 * `defaultOpen` is decided by the caller from what it knows — a section with
 * rows in it opens, an empty one does not — and remembered only for the life
 * of the panel. Nothing is hidden: the title stays on screen, so a person
 * looking for the attachments sees the section exists and opens it.
 */
export function PanelFold({
  title,
  summary,
  action,
  defaultOpen,
  children,
}: {
  title: string;
  /** What is inside, in a word or a number: "none", "2", "not sent". */
  summary?: string | undefined;
  /** The one control that belongs beside the title — usually a "+ Add". */
  action?: ReactNode;
  defaultOpen: boolean;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  // The caller often learns what the section holds AFTER first render (the
  // rows arrive from a query). A section that turns out to have something in
  // it opens itself once; it never closes itself, since somebody may have
  // opened it by hand to look.
  useEffect(() => {
    if (defaultOpen) {
      setOpen(true);
    }
  }, [defaultOpen]);
  const bodyId = useId();
  return (
    <section className="panel-fold" aria-label={title}>
      <div className="panel-fold-head">
        <button
          type="button"
          className="section-toggle panel-fold-toggle"
          aria-expanded={open}
          aria-controls={bodyId}
          onClick={() => setOpen((value) => !value)}
        >
          <span aria-hidden="true" className="section-toggle-caret">
            {open ? '▾' : '▸'}
          </span>
          <span className="panel-fold-title">{title}</span>
          {/* Spaced by CSS, not by a text space: the accessible-name algorithm
              trims each inline child, so the name reads "Subtasks· none"
              whatever whitespace is typed here. Tests match on the words. */}
          {summary === undefined ? null : <span className="panel-fold-summary">· {summary}</span>}
        </button>
        {action}
      </div>
      {open ? (
        <div id={bodyId} className="panel-fold-body">
          {children}
        </div>
      ) : null}
    </section>
  );
}
